import React from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import {
  signalBorders,
  signalFonts,
  signalRadii,
  signalShadows,
  signalStatus,
  signalSurfaces,
  signalText,
} from '@/theme/foundations/tokens';

interface ScreenFrameProps {
  children?: React.ReactNode;
  /** Two-digit index shown in the accent chip, e.g. "01". */
  index?: string;
  title?: string;
  /** Right-hand mono note, e.g. "DESKTOP · DARK". */
  note?: string;
  /** Fixed frame width. The design composes at 1420px. */
  width?: number | string;
  height?: number | string;
  /** Frame background. Defaults to the screen surface. */
  background?: string;
  /**
   * Drop the bordered shell and render children directly under the caption.
   * Four of the ten screens are canvases of free-standing glass cards rather
   * than one app window; those keep the caption but supply their own layout.
   */
  bare?: boolean;
  sx?: SxProps<Theme>;
  /** Inner sx applied to the bordered frame itself. */
  frameSx?: SxProps<Theme>;
}

/**
 * The captioned, bordered shell every screen sits inside on the design canvas.
 * Keeping the caption here means each screen file contains only its own UI.
 */
export const ScreenFrame: React.FC<ScreenFrameProps> = ({
  children,
  index,
  title,
  note,
  width = 1420,
  height,
  background = signalSurfaces.screen,
  bare = false,
  sx,
  frameSx,
}) => (
  <Box
    component="section"
    sx={{ display: 'flex', flexDirection: 'column', gap: '14px', ...sx }}
  >
    {(index || title || note) && (
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
        {index && (
          <Box
            component="span"
            sx={{
              fontFamily: signalFonts.mono,
              fontWeight: 600,
              fontSize: '11px',
              padding: '4px 8px',
              borderRadius: `${signalRadii.xs}px`,
              background: signalStatus.syncing.bg,
              color: signalStatus.syncing.fg,
            }}
          >
            {index}
          </Box>
        )}
        {title && (
          <Box
            component="h2"
            sx={{
              margin: 0,
              fontFamily: signalFonts.display,
              fontWeight: 700,
              fontSize: '24px',
              letterSpacing: '-0.02em',
              color: signalText.primary,
            }}
          >
            {title}
          </Box>
        )}
        {note && (
          <Box
            component="span"
            sx={{
              fontFamily: signalFonts.mono,
              fontWeight: 500,
              fontSize: '11px',
              color: signalText.muted,
            }}
          >
            {note}
          </Box>
        )}
      </Box>
    )}

    {bare ? (
      children
    ) : (
      <Box
        sx={{
          width,
          maxWidth: '100%',
          height,
          borderRadius: `${signalRadii['3xl']}px`,
          overflow: 'hidden',
          border: `1px solid ${signalBorders.medium}`,
          background,
          boxShadow: signalShadows.screen,
          ...frameSx,
        }}
      >
        {children}
      </Box>
    )}
  </Box>
);

export default ScreenFrame;
